import { useEffect, useState } from 'react'
import { api } from './api.js'
import { Login } from './pages/Login.js'
import { TotpEnroll, TotpVerify } from './pages/Totp.js'
import { Fleet } from './pages/Fleet.js'
import { AddProvider } from './pages/AddProvider.js'
import { Audit } from './pages/Audit.js'
import { Accounts } from './pages/Accounts.js'
import { Catalog } from './pages/Catalog.js'
import { Monitor } from './pages/Monitor.js'

type Me = Awaited<ReturnType<typeof api.auth.me.query>>
type Tab = 'monitor' | 'frota' | 'contas' | 'produtos' | 'provedor' | 'auditoria'

type Tema = 'claro' | 'escuro'

/**
 * Tema do console.
 *
 * Padrao CLARO, e nao a preferencia do sistema. O painel seguia
 * `prefers-color-scheme`, entao quem usa o Windows em modo escuro abria o
 * console no escuro sem ter pedido e sem ter como sair. A escolha agora e
 * explicita e sobrevive ao recarregamento.
 *
 * O acesso ao localStorage vai em try/catch porque ele lanca — nao devolve
 * nulo — em janela anonima e com cookies de terceiros bloqueados. Um console
 * de infraestrutura nao pode ficar em tela branca por causa da preferencia de
 * cor.
 */
function useTema(): [Tema, () => void] {
  const [tema, setTema] = useState<Tema>(() => {
    try {
      return localStorage.getItem('via360:tema') === 'escuro' ? 'escuro' : 'claro'
    } catch {
      return 'claro'
    }
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', tema === 'escuro' ? 'dark' : 'light')
    try {
      localStorage.setItem('via360:tema', tema)
    } catch {
      // Sem persistencia: a escolha vale so nesta aba, o que ainda e melhor
      // que ignorar o clique.
    }
  }, [tema])

  return [tema, () => setTema((t) => (t === 'claro' ? 'escuro' : 'claro'))]
}

export function App() {
  const [tema, alternarTema] = useTema()
  const [me, setMe] = useState<Me | undefined>(undefined)
  const [needsEnroll, setNeedsEnroll] = useState(false)
  const [tab, setTab] = useState<Tab>('monitor')

  const refresh = async () => {
    const res = await api.auth.me.query()
    setMe(res)
    if (res?.state === 'pending_totp') {
      // Distingue "nunca configurou" de "configurou e precisa digitar o codigo":
      // sao duas telas diferentes e confundi-las trava o usuario.
      try {
        await api.auth.totpStart.mutate()
        setNeedsEnroll(true)
      } catch {
        setNeedsEnroll(false)
      }
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  if (me === undefined) return <Splash>Carregando...</Splash>
  if (me === null) return <Login onDone={refresh} />
  if (me.state === 'pending_totp') {
    return needsEnroll ? <TotpEnroll onDone={refresh} /> : <TotpVerify onDone={refresh} />
  }

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-40 border-b border-line bg-panel/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
          <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
            <span className="h-2 w-2 rounded-full bg-accent" />
            Via 360
          </span>
          <nav className="flex gap-1">
            {(
              [
                ['monitor', 'Monitor'],
                ['frota', 'Frota'],
                ['contas', 'Contas'],
                ['produtos', 'Produtos'],
                ['provedor', 'Provedores'],
                ['auditoria', 'Auditoria'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`rounded-lg px-3 py-1.5 text-sm transition ${
                  tab === key
                    ? 'bg-accent-soft font-medium text-accent'
                    : 'text-ink-2 hover:bg-ground hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-ink-3">
              {me.user.name} · <span className="font-mono">{me.user.role}</span>
            </span>
            <button
              onClick={alternarTema}
              title={tema === 'claro' ? 'Mudar para o tema escuro' : 'Mudar para o tema claro'}
              aria-label={tema === 'claro' ? 'Mudar para o tema escuro' : 'Mudar para o tema claro'}
              className="rounded border border-line px-2 py-1 text-xs text-ink-3 hover:border-accent hover:text-accent"
            >
              {tema === 'claro' ? 'escuro' : 'claro'}
            </button>
            <button
              onClick={async () => {
                await api.auth.logout.mutate()
                setMe(null)
              }}
              className="text-xs text-ink-3 underline underline-offset-4 hover:text-accent"
            >
              sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {tab === 'monitor' && <Monitor onNavigate={setTab} />}
        {tab === 'frota' && <Fleet />}
        {tab === 'contas' && <Accounts />}
        {tab === 'produtos' && <Catalog />}
        {tab === 'provedor' && <AddProvider onAdded={() => setTab('frota')} />}
        {tab === 'auditoria' && <Audit />}
      </main>
    </div>
  )
}

function Splash({ children }: { children: React.ReactNode }) {
  return <div className="grid min-h-full place-items-center text-sm text-ink-3">{children}</div>
}
